import type { ComponentType } from 'react'
import { Icons } from '@/components/ui/icons'
import type { AppMessages } from '@/i18n/config'
import { canAccess } from '@/lib/roles'

export type NavItemId = keyof AppMessages['nav']['items']
export type NavSectionId = keyof AppMessages['nav']['sections']

/** One sidebar / palette entry. Visibility is decided by `canAccess(href, roles)`; the label is `nav.items.<id>`. */
export interface NavItem {
  href: string
  id: NavItemId
  icon: ComponentType<{ className?: string; strokeWidth?: number }>
}

/** A group of entries; its heading is `nav.sections.<id>`. */
export interface NavSection {
  id: NavSectionId
  items: NavItem[]
}

/** Single source for both sidebars. */
export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'platform',
    items: [
      { href: '/', id: 'dashboard', icon: Icons.dashboard },
      { href: '/invoices', id: 'invoices', icon: Icons.receipt },
      { href: '/agent-hub', id: 'agentHub', icon: Icons.bot },
      { href: '/data-manager', id: 'dataManager', icon: Icons.layers },
    ],
  },
  {
    id: 'aiIntelligence',
    items: [
      { href: '/ai/policies', id: 'aiPolicies', icon: Icons.brain },
      { href: '/ai/insights', id: 'aiInsights', icon: Icons.lightbulb },
    ],
  },
  {
    id: 'administration',
    items: [
      { href: '/admin/users', id: 'users', icon: Icons.users },
      { href: '/admin/roles', id: 'roles', icon: Icons.shield },
      { href: '/admin/groups', id: 'groups', icon: Icons.network },
      { href: '/admin/sessions', id: 'sessions', icon: Icons.device },
      { href: '/admin/events', id: 'events', icon: Icons.activity },
      { href: '/admin/audit', id: 'audit', icon: Icons.fileText },
      { href: '/admin/settings', id: 'adminSettings', icon: Icons.settings },
    ],
  },
  {
    id: 'system',
    items: [{ href: '/settings', id: 'settings', icon: Icons.settings }],
  },
]

/** Sections filtered to what `roles` may open; empty sections are dropped. */
export function visibleNavSections(roles?: readonly string[] | null): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => canAccess(item.href, roles)),
  })).filter((section) => section.items.length > 0)
}
